import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { customersApi, installationsApi, staffApi, inventoryApi, salesApi, servicePickupsApi } from '../api/client';

const PICKUP_STATUS = {
  with_us:    { label: 'With Us',              bg: '#dbeafe', color: '#1d4ed8' },
  at_service: { label: 'At Service Center',    bg: '#fef3c7', color: '#b45309' },
  returned:   { label: 'Returned to Customer', bg: '#d1fae5', color: '#065f46' },
};
import { CID, Spin, Modal, F, useFlash, parseErr, MultiSelect } from '../components/UI';
import { ArrowLeft, Receipt, AlertCircle, MessageCircle, Wrench, UserPlus, FileText, Edit, Calendar, User, Zap, Package, Trash, Printer } from '../components/Icons';
import { useAuth } from '../context/AuthContext';
import { STATES, getDistricts } from '../data/indiaLocations';

const DC = { sale:'d-sale', complaint:'d-comp', enquiry:'d-enq', service:'d-svc', onboard:'d-onb', bill:'d-bill', update:'d-bill' };
const DI = { sale: Receipt, complaint: AlertCircle, enquiry: MessageCircle, service: Wrench, onboard: UserPlus, bill: FileText, update: Edit };

const fmtDate = d => d ? new Date(d + 'T00:00:00').toLocaleDateString('en-IN', { dateStyle: 'medium' }) : '—';

function warrantyExpiry(installDate, months) {
  const d = new Date(installDate);
  d.setMonth(d.getMonth() + months);
  return d.toISOString().split('T')[0];
}
function warrantyStatus(expiry) {
  const today = new Date().toISOString().split('T')[0];
  if (expiry < today) return 'expired';
  const days = (new Date(expiry) - new Date()) / 86400000;
  return days <= 60 ? 'expiring' : 'active';
}
const WS_STYLE = {
  active:   { color:'var(--green)',  bg:'rgba(22,163,74,0.08)'  },
  expiring: { color:'orange',        bg:'rgba(251,146,60,0.1)'  },
  expired:  { color:'var(--red)',    bg:'rgba(220,53,69,0.08)'  },
};

const BLANK_INST = { product_name:'', product_id:'', serial_number:'', installation_date:'', warranty_months:'12', installed_by:'', notes:'' };
const BLANK_SALE = { product_id:'', qty:1, serial_number_id:'', payment_mode:'Cash', payment_status:'paid', amount_paid:'', followup_date:'', assigned_to:[] };

export default function CustomerProfile() {
  const { id } = useParams(); const navigate = useNavigate();
  const { canWrite, canDelete } = useAuth();
  const { show, Flash } = useFlash();
  const [c, setC]           = useState(null);
  const [act, setAct]       = useState([]);
  const [showAllAct, setShowAllAct] = useState(false);
  const [insts, setInsts]         = useState([]);
  const [pickups, setPickups]     = useState([]);
  const [pendingSales, setPending] = useState([]);
  const [staff, setStaff]   = useState([]);
  const [prods, setProds]   = useState([]);
  const [busy, setBusy]     = useState(true);

  // Customer edit modal
  const [showEdit, setShowEdit]   = useState(false);
  const [editForm, setEditForm]   = useState({});
  const [editSaving, setES]       = useState(false);
  const setEF = k => e => setEditForm(f => ({ ...f, [k]: e.target.value }));

  const openEdit = () => {
    setEditForm({
      salutation: c.salutation || '',
      full_name:  c.full_name,
      address:    c.address,
      district:   c.district,
      state:      c.state,
      pincode:    c.pincode || '',
      phone:      c.phone,
      email:      c.email || '',
      status:     c.status,
    });
    setShowEdit(true);
  };

  const saveEdit = async () => {
    if (!editForm.full_name || !editForm.phone || !editForm.district) {
      show('Name, phone, and district are required.', 'error'); return;
    }
    setES(true);
    try {
      const { data } = await customersApi.update(id, { ...editForm, email: editForm.email || null });
      setC(data);
      show('Customer details updated!');
      setShowEdit(false);
    } catch(e) { show(parseErr(e, 'Failed to update.'), 'error'); }
    finally { setES(false); }
  };

  // Install modal
  const [showInst, setShowInst]   = useState(false);
  const [editInst, setEditInst]   = useState(null);
  const [instForm, setInstForm]   = useState(BLANK_INST);
  const [instSaving, setIS]       = useState(false);
  const setIF = k => e => setInstForm(f => ({ ...f, [k]: e.target.value }));

  // Take for Service modal
  const [showPickup, setShowPickup]     = useState(false);
  const [pickupInst, setPickupInst]     = useState(null);
  const [pickupSerial, setPickupSerial] = useState('');
  const [pickupSaving, setPickupSaving] = useState(false);

  // Sale modal
  const [showSale, setShowSale]       = useState(false);
  const [saleForm, setSaleForm]       = useState(BLANK_SALE);
  const [saleDiscVal, setSaleDiscVal] = useState('');
  const [saleSaving, setSaleSaving]   = useState(false);
  const [saleErr, setSaleErr]         = useState('');
  const [saleBill, setSaleBill]       = useState(null);
  const setSF = k => e => setSaleForm(f => ({ ...f, [k]: e.target.value }));

  // Fill in serial_number on installations that lack one but have a matching sale item with a serial
  const enrichInsts = (ins, sales) => ins.map(inst => {
    if (inst.serial_number || !inst.product_id) return inst;
    for (const sale of sales) {
      const item = (sale.items || []).find(i => i.product_id === inst.product_id && i.serial_number);
      if (item) return { ...inst, serial_number: item.serial_number };
    }
    return inst;
  });

  useEffect(() => {
    Promise.all([
      customersApi.get(id),
      customersApi.activity(id),
      installationsApi.list(id),
      inventoryApi.list(),
      salesApi.list({ customer_id: id, page_size: 200 }),
    ])
      .then(([{ data: cu }, { data: a }, { data: ins }, { data: pr }, { data: sl }]) => {
        setC(cu); setAct(a); setProds(pr);
        setPending(sl.filter(s => s.payment_status === 'partial' || s.payment_status === 'pending'));
        setInsts(enrichInsts(ins, sl));
      })
      .catch(() => navigate('/customers'))
      .finally(() => setBusy(false));

    staffApi.list().then(({ data }) => setStaff(data)).catch(() => {});
    servicePickupsApi.list({ customer_id: id, page_size: 200 }).then(({ data }) => setPickups(data)).catch(() => {});
  }, [id, navigate]);

  const openAddInst = () => { setEditInst(null); setInstForm(BLANK_INST); setShowInst(true); };
  const openEditInst = inst => {
    setEditInst(inst);
    setInstForm({
      product_name:     inst.product_name,
      product_id:       inst.product_id || '',
      serial_number:    inst.serial_number || '',
      installation_date: inst.installation_date,
      warranty_months:  String(inst.warranty_months),
      installed_by:     inst.installed_by || '',
      notes:            inst.notes || '',
    });
    setShowInst(true);
  };

  const saveInst = async () => {
    if (!instForm.product_name || !instForm.installation_date) { show('Product name and installation date are required.', 'error'); return; }
    setIS(true);
    try {
      const payload = {
        product_name:      instForm.product_name,
        product_id:        instForm.product_id || null,
        serial_number:     instForm.serial_number.trim() || null,
        installation_date: instForm.installation_date,
        warranty_months:   parseInt(instForm.warranty_months) || 12,
        installed_by:      instForm.installed_by || null,
        notes:             instForm.notes || null,
      };
      if (editInst) {
        const { data } = await installationsApi.update(editInst.id, payload);
        setInsts(r => r.map(x => x.id === data.id ? data : x));
        show('Installation updated!');
      } else {
        const { data } = await installationsApi.create(id, payload);
        setInsts(r => [data, ...r]);
        show('Installation added!');
      }
      setShowInst(false);
    } catch(e) { show(parseErr(e, 'Failed to save.'), 'error'); }
    finally { setIS(false); }
  };

  const openSale = () => {
    setSaleForm(BLANK_SALE);
    setSaleDiscVal('');
    setSaleErr('');
    setShowSale(true);
  };

  const saveSale = async () => {
    if (!saleForm.product_id) { setSaleErr('Please select a product.'); return; }
    const selProd = prods.find(p => p.id === saleForm.product_id);
    if (!selProd) { setSaleErr('Invalid product selection.'); return; }
    setSaleSaving(true); setSaleErr('');
    try {
      const subtotal = Number(selProd.mrp) * parseInt(saleForm.qty || 1);
      const discPct  = saleDiscVal && subtotal > 0 ? (Number(saleDiscVal) / subtotal) * 100 : 0;
      const saleItem = { product_id: saleForm.product_id, qty: parseInt(saleForm.qty), unit_price: selProd.mrp, discount: 0 };
      if (saleForm.serial_number_id) saleItem.serial_number_id = saleForm.serial_number_id;
      const payload = {
        customer_id:    c.id,
        items:          [saleItem],
        discount:       discPct,
        notes:          discPct > 0 ? 'Under Exchange' : undefined,
        payment_mode:   saleForm.payment_mode,
        payment_status: saleForm.payment_status,
      };
      if (saleForm.amount_paid !== '') payload.amount_paid = Number(saleForm.amount_paid);
      if (saleForm.followup_date)      payload.followup_date = saleForm.followup_date;
      if (saleForm.assigned_to.length > 0) payload.assigned_to = saleForm.assigned_to;
      const { data: newSale } = await salesApi.create(payload);
      const [{ data: sl }, { data: a }, { data: pr }, { data: ins }] = await Promise.all([
        salesApi.list({ customer_id: id, page_size: 200 }),
        customersApi.activity(id),
        inventoryApi.list(),
        installationsApi.list(id),
      ]);
      setPending(sl.filter(s => s.payment_status === 'partial' || s.payment_status === 'pending'));
      setAct(a);
      setProds(pr);
      setInsts(enrichInsts(ins, sl));
      show('Sale recorded!');
      setShowSale(false);
      setSaleBill(newSale);
    } catch(e) { setSaleErr(parseErr(e, 'Failed to record sale.')); }
    finally { setSaleSaving(false); }
  };

  const openTakeForService = inst => {
    setPickupInst(inst);
    setPickupSerial(inst.serial_number || '');
    setShowPickup(true);
  };

  const confirmTakeForService = async () => {
    setPickupSaving(true);
    try {
      const today = new Date().toISOString().split('T')[0];
      const sn = pickupSerial.trim() || null;
      await servicePickupsApi.create({
        customer_id:      c.id,
        customer_name:    c.full_name,
        customer_address: [c.address, c.district].filter(Boolean).join(', '),
        product_name:     pickupInst.product_name,
        serial_number:    sn,
        installation_id:  pickupInst.id,
        taken_date:       today,
        status:           'with_us',
      });
      // If user entered a serial number and the installation didn't have one, save it back
      if (sn && !pickupInst.serial_number) {
        const { data: updated } = await installationsApi.update(pickupInst.id, { serial_number: sn });
        setInsts(r => r.map(x => x.id === updated.id ? updated : x));
      }
      show(`"${pickupInst.product_name}" added to Service Pickups.`);
      setShowPickup(false);
    } catch { show('Failed to create service pickup record.', 'error'); }
    finally { setPickupSaving(false); }
  };

  const delInst = async inst => {
    if (!window.confirm(`Remove ${inst.product_name}?`)) return;
    try {
      await installationsApi.delete(inst.id);
      setInsts(r => r.filter(x => x.id !== inst.id));
      show('Removed.');
    } catch { show('Failed.', 'error'); }
  };

  const handleProdSelect = e => {
    const prod = prods.find(p => p.id === e.target.value);
    setInstForm(f => ({
      ...f,
      product_id:      e.target.value,
      product_name:    prod ? prod.name : f.product_name,
      warranty_months: prod?.warranty_months ? String(prod.warranty_months) : f.warranty_months,
    }));
  };

  const printSaleBill = sale => {
    const discAmt = Number(sale.subtotal) * Number(sale.discount || 0) / 100;
    const isExchange = sale.notes && sale.notes.toLowerCase().includes('exchange');
    const itemRows = (sale.items || []).map((item, i) => {
      const wm = item.product?.warranty_months || 0;
      const warrantyTag = wm > 0 ? ` <span style="font-size:10px;color:#2563eb">(${wm} months Warranty)</span>` : '';
      const snTag = item.serial_number ? `<br/><span style="font-size:10.5px;color:#888">S/N: ${item.serial_number}</span>` : '';
      return `<tr>
        <td style="padding:7px 10px;color:#666;text-align:center">${i+1}</td>
        <td style="padding:7px 10px">${item.product?.name || '—'}${warrantyTag}${snTag}</td>
        <td style="padding:7px 10px;text-align:center;color:#666">${item.product?.hsn_code || '8541'}</td>
        <td style="padding:7px 10px;text-align:center">${item.qty}</td>
        <td style="padding:7px 10px;text-align:right">&#8377;${Number(item.unit_price).toLocaleString('en-IN')}</td>
        <td style="padding:7px 10px;text-align:right;font-weight:600">&#8377;${Number(item.qty*item.unit_price).toLocaleString('en-IN')}</td>
      </tr>`;
    }).join('');
    const discRow = discAmt > 0 ? `
      <tr><td colspan="2" style="text-align:right;padding:4px 6px;color:green;font-size:12px">${isExchange ? 'Under Exchange' : 'Discount'}</td>
      <td style="text-align:right;padding:4px 6px;color:green;font-size:12px">&#8722; &#8377;${discAmt.toLocaleString('en-IN',{minimumFractionDigits:2,maximumFractionDigits:2})}</td></tr>` : '';
    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"/><title>Invoice</title>
    <style>
      @page { size: A4; margin: 15mm; }
      * { box-sizing: border-box; margin: 0; padding: 0; }
      body { font-family: Arial, sans-serif; font-size: 13px; color: #111; }
      .header { display: flex; justify-content: space-between; align-items: flex-start; border-bottom: 2px solid #1e3a5f; padding-bottom: 12px; margin-bottom: 16px; }
      .company-name { font-size: 18px; font-weight: 700; color: #1e3a5f; margin-bottom: 4px; }
      .company-info { font-size: 11px; color: #666; line-height: 1.6; }
      table { width: 100%; border-collapse: collapse; margin-bottom: 14px; font-size: 12px; }
      thead tr { background: #1e3a5f; color: #fff; }
      thead th { padding: 8px 10px; text-align: left; }
      tbody tr { border-bottom: 1px solid #e5e7eb; }
      .totals-table { width: 260px; margin-left: auto; font-size: 12px; }
      .totals-table td { padding: 4px 6px; }
      .grand-total td { font-size: 15px; font-weight: 700; color: #1e3a5f; border-top: 2px solid #1e3a5f; padding-top: 8px; }
      .footer { margin-top: 24px; padding-top: 10px; border-top: 1px solid #e5e7eb; font-size: 11px; color: #888; text-align: center; }
    </style></head><body>
    <div class="header">
      <div>
        <div class="company-name">Sakthi Solar Solutions</div>
        <div class="company-info">
          Sakthi Arcade, Market Jn, Kandiyoor, Thattarambalam PO, Mavelikara - 690103<br/>
          Ph: 9446191276, 9846065984, 0479 2305650, 2309650<br/>
          Email: info@sakthiinverters.com &nbsp;|&nbsp; www.sakthiinverters.com
        </div>
      </div>
      <div style="text-align:right">
        <div style="font-size:15px;font-weight:700;color:#1e3a5f;letter-spacing:2px">INVOICE</div>
        <div style="font-size:11px;color:#666;margin-top:4px;line-height:1.7">
          Date: ${new Date(sale.created_at).toLocaleDateString('en-IN',{dateStyle:'medium'})}
        </div>
      </div>
    </div>
    <div style="background:#f5f7fa;border-radius:6px;padding:10px 14px;margin-bottom:14px;font-size:12px">
      <div style="font-weight:700;color:#1e3a5f;margin-bottom:4px">Bill To:</div>
      <div style="font-weight:600">${c.full_name}</div>
      <div style="color:#666">Customer ID: ${c.customer_code}</div>
      <div style="color:#666">Ph: ${c.phone}</div>
      <div style="color:#666">${c.address}</div>
    </div>
    <table>
      <thead><tr>
        <th style="width:36px;text-align:center">#</th>
        <th>Product / Description</th>
        <th style="width:60px;text-align:center">HSN</th>
        <th style="width:50px;text-align:center">Qty</th>
        <th style="width:90px;text-align:right">Unit Price</th>
        <th style="width:90px;text-align:right">Amount</th>
      </tr></thead>
      <tbody>${itemRows}</tbody>
    </table>
    <table class="totals-table">
      <tbody>
        <tr><td style="color:#666">Subtotal</td><td style="text-align:right">&#8377;${Number(sale.subtotal).toLocaleString('en-IN')}</td></tr>
        ${discRow}
        <tr class="grand-total"><td>Total</td><td style="text-align:right">&#8377;${Number(sale.total).toLocaleString('en-IN')}</td></tr>
      </tbody>
    </table>
    <div style="font-size:12px;display:flex;gap:24px">
      <span><strong>Payment Mode:</strong> ${sale.payment_mode || '—'}</span>
      <span><strong>Status:</strong> ${sale.payment_status}</span>
    </div>
    <div class="footer">Thank you for your business! &nbsp;|&nbsp; This is a computer-generated invoice.<br/>
      <span style="font-size:9px;color:#bbb">Developed by Data Systems Intelligence Pvt Ltd</span>
    </div>
    </body></html>`;
    const w = window.open('', '_blank', 'width=850,height=1100');
    w.document.write(html);
    w.document.close();
    w.onload = () => { w.focus(); w.print(); };
  };

  if (busy) return <Spin label="Loading profile…"/>;
  if (!c) return null;

  const saleSelProd = prods.find(p => p.id === saleForm.product_id) || null;

  return (
    <div>
      <Flash/>
      <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:18 }}>
        <button className="btn btn-sm" onClick={() => navigate('/customers')}><ArrowLeft s={13}/> Back</button>
        <CID code={c.customer_code}/>
        <span style={{ fontSize:17, fontWeight:600, color:'var(--navy)' }}>{c.salutation ? `${c.salutation} ${c.full_name}` : c.full_name}</span>
        <span className={`badge ${c.status === 'active' ? 'b-ok' : 'b-gray'}`}>{c.status}</span>
      </div>

      <div className="two-col">
        <div>
          <div className="card">
            <div className="card-hd">
              <div className="card-title"><User s={15}/> Customer Info</div>
              {canWrite && <button className="btn btn-sm" onClick={openEdit}><Edit s={12}/> Edit</button>}
            </div>
            <div className="card-body" style={{ padding:'0 20px' }}>
              {[['Phone',c.phone],['Email',c.email||'—'],['Address',c.address],['District',c.district],['State',c.state],['Pincode',c.pincode||'—'],['Since',new Date(c.created_at).toLocaleDateString('en-IN')]].map(([l,v]) => (
                <div key={l} style={{ display:'flex', gap:12, padding:'8px 0', borderBottom:'1px solid var(--border)', fontSize:13 }}>
                  <span style={{ color:'var(--text2)', minWidth:72, fontSize:12, fontWeight:600 }}>{l}</span>
                  <span>{v}</span>
                </div>
              ))}
              {c.location_lat && c.location_lng && (
                <div style={{ display:'flex', gap:12, padding:'8px 0', borderBottom:'1px solid var(--border)', fontSize:13 }}>
                  <span style={{ color:'var(--text2)', minWidth:72, fontSize:12, fontWeight:600 }}>GPS</span>
                  <a href={`https://www.google.com/maps?q=${c.location_lat},${c.location_lng}`} target="_blank" rel="noopener noreferrer"
                    style={{ color:'var(--navy)', fontWeight:600, fontSize:13 }}>
                    Open in Google Maps
                  </a>
                </div>
              )}
            </div>
          </div>

          <div className="card">
            <div className="card-hd"><div className="card-title"><Zap s={15}/> Quick Actions</div></div>
            <div className="card-body" style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
              <button className="btn btn-sm" onClick={() => navigate('/complaints')}>+ Complaint</button>
              <button className="btn btn-sm" onClick={() => navigate('/service')}>+ Service</button>
              {canWrite && <button className="btn btn-sm btn-primary" onClick={openSale}>+ Sale</button>}
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-hd"><div className="card-title"><Calendar s={15}/> Activity Timeline</div></div>
          <div className="card-body">
            {act.length === 0
              ? <div style={{ fontSize:13, color:'var(--text2)' }}>No activity yet.</div>
              : <>
                  <div className="tl">
                    {(showAllAct ? act : act.slice(0, 5)).map(l => {
                      const IconC = DI[l.entity_type] || FileText;
                      return (
                        <div key={l.id} className="tl-row">
                          <div className={`tl-dot ${DC[l.entity_type] || 'd-bill'}`}><IconC s={14}/></div>
                          <div className="tl-body">
                            <div className="tl-title">{l.title}</div>
                            <div className="tl-when">{new Date(l.created_at).toLocaleString('en-IN', { dateStyle:'medium', timeStyle:'short' })}</div>
                            {l.description && <div className="tl-desc">{l.description}</div>}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  {act.length > 5 && (
                    <button className="btn btn-sm btn-ghost" style={{ marginTop:8, width:'100%', fontSize:12 }}
                      onClick={() => setShowAllAct(v => !v)}>
                      {showAllAct ? 'Show less' : `Show all ${act.length} activities`}
                    </button>
                  )}
                </>
            }
          </div>
        </div>
      </div>

      {/* ── Pending Payments ── */}
      {pendingSales.length > 0 && (
        <div className="card" style={{ marginTop:16, borderLeft:'3px solid var(--amber)' }}>
          <div className="card-hd">
            <div className="card-title" style={{ color:'var(--amber)' }}>
              <Receipt s={15}/> Pending Payments
            </div>
            <span style={{ fontSize:12, color:'var(--text2)' }}>{pendingSales.length} outstanding</span>
          </div>
          <div className="card-body" style={{ padding:0 }}>
            <div className="tbl-wrap"><table>
              <thead><tr>
                <th>Date</th><th>Items</th><th>Total</th><th>Paid</th><th>Balance</th><th>Mode</th><th>Status</th><th>Follow-up</th>
              </tr></thead>
              <tbody>
                {pendingSales.map(s => {
                  const paid    = Number(s.amount_paid || 0);
                  const total   = Number(s.total);
                  const balance = total - paid;
                  return (
                    <tr key={s.id}>
                      <td style={{ fontSize:12 }}>{new Date(s.created_at).toLocaleDateString('en-IN', { dateStyle:'medium' })}</td>
                      <td style={{ fontSize:12, color:'var(--text2)' }}>
                        {s.items?.map(i => `${i.product?.name || '—'} ×${i.qty}`).join(', ') || '—'}
                      </td>
                      <td style={{ fontWeight:600 }}>₹{total.toLocaleString('en-IN')}</td>
                      <td style={{ color:'var(--green)', fontWeight:600 }}>
                        {paid > 0 ? `₹${paid.toLocaleString('en-IN')}` : '—'}
                      </td>
                      <td style={{ color:'var(--red)', fontWeight:700 }}>₹{balance.toLocaleString('en-IN')}</td>
                      <td style={{ fontSize:12 }}>{s.payment_mode || '—'}</td>
                      <td><span className={`badge ${s.payment_status === 'partial' ? 'b-warn' : 'b-err'}`}>{s.payment_status}</span></td>
                      <td style={{ fontSize:12, color: s.followup_date ? 'var(--amber)' : 'var(--text2)', fontWeight: s.followup_date ? 600 : 400 }}>
                        {s.followup_date ? new Date(s.followup_date + 'T00:00:00').toLocaleDateString('en-IN', { dateStyle:'medium' }) : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table></div>
          </div>
        </div>
      )}

      {/* ── Installed Products ── */}
      <div className="card" style={{ marginTop:16 }}>
        <div className="card-hd">
          <div className="card-title"><Package s={15}/> Installed Products</div>
          {canWrite && <button className="btn btn-sm btn-primary" onClick={openAddInst}>+ Add Installation</button>}
        </div>
        <div className="card-body" style={{ padding:0 }}>
          {insts.length === 0
            ? <div style={{ padding:'20px', fontSize:13, color:'var(--text2)' }}>No installations recorded yet.</div>
            : <div className="tbl-wrap"><table>
                <thead><tr>
                  <th>Product</th><th>Serial No.</th><th>Installed On</th><th>Warranty</th><th>Expires On</th><th>Status</th><th>Installed By</th><th>Notes</th>
                  {canWrite && <th>Actions</th>}
                </tr></thead>
                <tbody>
                  {insts.map(inst => {
                    const expiry = warrantyExpiry(inst.installation_date, inst.warranty_months);
                    const ws     = warrantyStatus(expiry);
                    const wStyle = WS_STYLE[ws];
                    return (
                      <tr key={inst.id}>
                        <td style={{ fontWeight:600 }}>{inst.product_name}</td>
                        <td style={{ fontSize:12, fontFamily:'monospace', color:'var(--text2)' }}>{inst.serial_number || '—'}</td>
                        <td style={{ fontSize:12 }}>{fmtDate(inst.installation_date)}</td>
                        <td style={{ fontSize:12 }}>{inst.warranty_months} months</td>
                        <td style={{ fontSize:12 }}>{fmtDate(expiry)}</td>
                        <td>
                          <span style={{ fontSize:11, fontWeight:600, color:wStyle.color, background:wStyle.bg, borderRadius:4, padding:'2px 8px' }}>
                            {ws === 'expiring' ? 'Expiring Soon' : ws.charAt(0).toUpperCase() + ws.slice(1)}
                          </span>
                        </td>
                        <td style={{ fontSize:12 }}>{inst.assigned_staff_names && inst.assigned_staff_names.length > 0 ? inst.assigned_staff_names.join(', ') : (inst.installer_name || '—')}</td>
                        <td style={{ fontSize:12, color:'var(--text2)', maxWidth:160, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{inst.notes || '—'}</td>
                        {canWrite && <td style={{ display:'flex', gap:4 }}>
                          <button className="btn btn-sm" onClick={() => openEditInst(inst)} title="Edit"><Edit s={12}/></button>
                          <button className="btn btn-sm" onClick={() => openTakeForService(inst)} title="Take for Service" style={{ color:'#b45309', borderColor:'#f59e0b' }}><Wrench s={12}/></button>
                          {canDelete && <button className="btn btn-sm btn-danger" onClick={() => delInst(inst)} title="Delete"><Trash s={12}/></button>}
                        </td>}
                      </tr>
                    );
                  })}
                </tbody>
              </table></div>
          }
        </div>
      </div>

      {/* ── Service Pickups ── */}
      {pickups.length > 0 && (
        <div className="card" style={{ marginTop: 16 }}>
          <div className="card-hd">
            <div className="card-title"><Wrench s={15}/> Service Pickups</div>
          </div>
          <div className="card-body" style={{ padding: 0 }}>
            <div className="tbl-wrap"><table>
              <thead><tr>
                <th>Code</th><th>Product</th><th>Serial No.</th><th>Status</th>
                <th>Taken From Customer</th><th>Sent to Service</th><th>Returned from Service</th><th>Returned to Customer</th>
              </tr></thead>
              <tbody>
                {pickups.map(p => {
                  const ss = PICKUP_STATUS[p.status] || PICKUP_STATUS.with_us;
                  return (
                    <tr key={p.id}>
                      <td style={{ fontFamily:'monospace', fontSize:11, color:'var(--navy)', fontWeight:600 }}>{p.pickup_code}</td>
                      <td style={{ fontWeight:500 }}>{p.product_name}</td>
                      <td style={{ fontSize:12, fontFamily:'monospace', color:'var(--text2)' }}>{p.serial_number || '—'}</td>
                      <td>
                        <span style={{ fontSize:11, fontWeight:600, padding:'2px 10px', borderRadius:20, background:ss.bg, color:ss.color }}>
                          {ss.label}
                        </span>
                      </td>
                      <td style={{ fontSize:12 }}>{p.taken_date ? fmtDate(p.taken_date) : '—'}</td>
                      <td style={{ fontSize:12 }}>{p.sent_to_service_date ? fmtDate(p.sent_to_service_date) : '—'}</td>
                      <td style={{ fontSize:12 }}>{p.returned_date ? fmtDate(p.returned_date) : '—'}</td>
                      <td style={{ fontSize:12 }}>{p.returned_to_customer_date ? fmtDate(p.returned_to_customer_date) : '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table></div>
          </div>
        </div>
      )}

      {/* Add / Edit Installation Modal */}
      <Modal open={showInst} onClose={() => setShowInst(false)} title={editInst ? 'Edit Installation' : 'Add Installation'}
        footer={<><button className="btn" onClick={() => setShowInst(false)}>Cancel</button><button className="btn btn-primary" onClick={saveInst} disabled={instSaving}>{instSaving ? 'Saving…' : editInst ? 'Save Changes' : 'Add'}</button></>}
      >
        <div className="fg-2">
          <F label="Pick from Inventory" full>
            <select value={instForm.product_id} onChange={handleProdSelect}>
              <option value="">— Select product or type manually below —</option>
              {prods.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </F>
          <F label="Product Name" req full><input value={instForm.product_name} onChange={setIF('product_name')} placeholder="e.g. Luminous 3.5kVA Inverter"/></F>
          <F label="Serial Number" full><input value={instForm.serial_number} onChange={setIF('serial_number')} placeholder="e.g. LUM-2024-XXXXX"/></F>
          <F label="Installation Date" req><input type="date" value={instForm.installation_date} onChange={setIF('installation_date')}/></F>
          <F label="Warranty (months)" req><input type="number" min="1" value={instForm.warranty_months} onChange={setIF('warranty_months')}/></F>
          <F label="Installed By" full>
            <select value={instForm.installed_by} onChange={setIF('installed_by')}>
              <option value="">— Select staff —</option>
              {staff.filter(s => s.is_active).map(s => <option key={s.id} value={s.id}>{s.name} ({s.role})</option>)}
            </select>
          </F>
          <F label="Notes" full><textarea value={instForm.notes} onChange={setIF('notes')} placeholder="Any additional notes…" style={{ minHeight:55 }}/></F>
        </div>
      </Modal>

      {/* New Sale Modal */}
      <Modal open={showSale} onClose={() => setShowSale(false)}
        title={`New Sale — ${c.full_name}`}
        footer={<>
          <button className="btn" onClick={() => setShowSale(false)}>Cancel</button>
          <button className="btn btn-primary" onClick={saveSale} disabled={saleSaving}>
            {saleSaving ? 'Saving…' : 'Save & Generate Bill'}
          </button>
        </>}
      >
        {saleErr && <div className="notice n-err" style={{ marginBottom:10 }}>{saleErr}</div>}
        <div className="notice n-ok" style={{ marginBottom:12, fontSize:13, display:'flex', alignItems:'center', gap:8 }}>
          <strong>{c.full_name}</strong>
          <CID code={c.customer_code}/>
          <span style={{ color:'var(--text2)', fontSize:12 }}>{c.phone}</span>
        </div>
        <div className="fg-2">
          <F label="Product" full>
            <select value={saleForm.product_id} onChange={e => setSaleForm(f => ({ ...f, product_id: e.target.value, serial_number_id: '' }))}>
              <option value="">-- Select Product --</option>
              {prods.map(p => <option key={p.id} value={p.id}>{p.name} — ₹{Number(p.mrp).toLocaleString('en-IN')} (Stock: {p.stock_qty})</option>)}
            </select>
          </F>

          {saleSelProd && saleSelProd.stock_qty === 0 && (
            <div style={{ gridColumn:'1/-1', padding:'8px 12px', background:'#fff3cd', border:'1px solid #ffc107', borderRadius:6, fontSize:12, color:'#856404', fontWeight:500 }}>
              Out of Stock — this product has no inventory available.
            </div>
          )}

          {saleSelProd && (() => {
            const inStock = (saleSelProd.serials || []).filter(s => s.status === 'in_stock');
            return inStock.length > 0 ? (
              <F label="Serial Number" full>
                <select value={saleForm.serial_number_id} onChange={setSF('serial_number_id')}>
                  <option value="">-- Select Serial Number (optional) --</option>
                  {inStock.map(s => <option key={s.id} value={s.id}>{s.serial_number}</option>)}
                </select>
              </F>
            ) : (
              <div style={{ gridColumn:'1/-1', padding:'7px 10px', background:'var(--surface)', border:'1px solid var(--border)', borderRadius:6, fontSize:12, color:'var(--text2)' }}>
                No serial numbers tracked for this product. Add serials via Inventory to enable tracking.
              </div>
            );
          })()}

          <F label="Quantity"><input type="number" min="1" value={saleForm.qty} onChange={setSF('qty')}/></F>

          <div style={{ gridColumn:'1/-1', padding:'10px 12px', background:'var(--surface)', borderRadius:8, border:'1px solid var(--border)' }}>
            <div style={{ fontSize:12, fontWeight:600, color:'var(--text2)', textTransform:'uppercase', marginBottom:8 }}>Under Exchange Discount (optional)</div>
            <div style={{ display:'flex', alignItems:'center', gap:8 }}>
              <span style={{ padding:'5px 14px', fontSize:13, fontWeight:600, borderRadius:6, background:'var(--primary)', color:'#fff', whiteSpace:'nowrap' }}>₹ Fixed</span>
              <input type="number" min="0" step="0.01" value={saleDiscVal}
                onChange={e => setSaleDiscVal(e.target.value)} placeholder="0.00" style={{ width:120 }}/>
              {saleDiscVal > 0 && <span style={{ fontSize:12, color:'var(--green)', fontWeight:600 }}>
                − ₹{Number(saleDiscVal).toLocaleString('en-IN')} off
              </span>}
            </div>
          </div>

          {saleSelProd && (() => {
            const subtotal   = Number(saleSelProd.mrp) * (parseInt(saleForm.qty) || 1);
            const discAmt    = saleDiscVal ? Math.min(Number(saleDiscVal), subtotal) : 0;
            const grandTotal = subtotal - discAmt;
            return (
              <div style={{ gridColumn:'1/-1', padding:'10px 14px', background:'var(--surface)', borderRadius:8, fontSize:13 }}>
                <div style={{ display:'flex', justifyContent:'space-between', marginBottom:3, color:'var(--text2)' }}><span>Subtotal</span><span>₹{subtotal.toLocaleString('en-IN')}</span></div>
                {discAmt > 0 && <div style={{ display:'flex', justifyContent:'space-between', marginBottom:3, color:'var(--green)' }}><span>Under Exchange</span><span>− ₹{discAmt.toLocaleString('en-IN', { maximumFractionDigits:2 })}</span></div>}
                <div style={{ display:'flex', justifyContent:'space-between', fontWeight:700, borderTop:'1px solid var(--border)', paddingTop:4, marginTop:4, color:'var(--navy)' }}><span>Total</span><span>₹{grandTotal.toLocaleString('en-IN', { maximumFractionDigits:2 })}</span></div>
              </div>
            );
          })()}

          <F label="Assigned To" full>
            <MultiSelect
              options={staff.filter(s => s.is_active && s.role === 'field').map(s => ({ value: s.id, label: s.name }))}
              value={saleForm.assigned_to}
              onChange={ids => setSaleForm(f => ({ ...f, assigned_to: ids }))}
            />
          </F>

          <F label="Payment Mode">
            <select value={saleForm.payment_mode} onChange={setSF('payment_mode')}>
              {['Cash','Card','UPI','Bank Transfer'].map(m => <option key={m}>{m}</option>)}
            </select>
          </F>
          <F label="Payment Received">
            <select value={saleForm.payment_status} onChange={setSF('payment_status')}>
              <option value="paid">Full Payment Received</option>
              <option value="partial">Part Payment</option>
              <option value="pending">Not Yet Paid</option>
            </select>
          </F>
          {saleForm.payment_status === 'partial' && (
            <>
              <F label="Amount Paid Now (₹)">
                <input type="number" min="0" placeholder="Amount received so far"
                  value={saleForm.amount_paid} onChange={setSF('amount_paid')}/>
              </F>
              <F label="Follow-up Date">
                <input type="date" value={saleForm.followup_date} onChange={setSF('followup_date')}/>
              </F>
            </>
          )}
        </div>
      </Modal>

      {/* Take for Service Modal */}
      <Modal open={showPickup} onClose={() => setShowPickup(false)}
        title="Take for Service"
        footer={<>
          <button className="btn" onClick={() => setShowPickup(false)}>Cancel</button>
          <button className="btn btn-primary" onClick={confirmTakeForService} disabled={pickupSaving}>
            {pickupSaving ? 'Creating…' : 'Confirm Pickup'}
          </button>
        </>}
      >
        {pickupInst && (
          <div className="fg-2">
            <F label="Product" full>
              <input value={pickupInst.product_name} readOnly style={{ background:'var(--surface)', color:'var(--text2)' }}/>
            </F>
            <F label="Serial Number" full>
              <input
                value={pickupSerial}
                onChange={e => setPickupSerial(e.target.value)}
                placeholder="Enter serial number (if available)"
                autoFocus
              />
            </F>
            <div style={{ gridColumn:'1/-1', fontSize:12, color:'var(--text2)', padding:'6px 10px', background:'#fffbeb', border:'1px solid #fde68a', borderRadius:6 }}>
              This will create a Service Pickup record. The serial number will also be updated on the installed product.
            </div>
          </div>
        )}
      </Modal>

      {/* Sale Bill Modal */}
      <Modal open={!!saleBill} onClose={() => setSaleBill(null)} title="Sale Invoice"
        footer={<>
          <button className="btn" onClick={() => setSaleBill(null)}>Close</button>
          <button className="btn btn-primary" onClick={() => printSaleBill(saleBill)}><Printer s={14}/> Print Bill</button>
        </>}
      >
        {saleBill && (() => {
          const discAmt = Number(saleBill.subtotal) * Number(saleBill.discount || 0) / 100;
          const isExchange = saleBill.notes && saleBill.notes.toLowerCase().includes('exchange');
          return (
            <div style={{ fontSize:13, color:'#111' }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:14, paddingBottom:12, borderBottom:'2px solid var(--navy)' }}>
                <div>
                  <div style={{ fontSize:17, fontWeight:700, color:'var(--navy)' }}>Sakthi Solar Solutions</div>
                  <div style={{ fontSize:11.5, color:'var(--text2)', marginTop:3 }}>Sakthi Arcade, Market Jn, Kandiyoor, Mavelikara - 690103</div>
                  <div style={{ fontSize:11.5, color:'var(--text2)' }}>Ph: 9446191276, 9846065984</div>
                </div>
                <div style={{ textAlign:'right' }}>
                  <div style={{ fontSize:13, fontWeight:700, color:'var(--navy)', letterSpacing:1 }}>INVOICE</div>
                  <div style={{ fontSize:11.5, color:'var(--text2)', marginTop:4 }}>
                    {new Date(saleBill.created_at).toLocaleDateString('en-IN',{dateStyle:'medium'})}
                  </div>
                </div>
              </div>
              <div style={{ background:'var(--surface)', borderRadius:6, padding:'8px 12px', marginBottom:12, fontSize:12 }}>
                <div style={{ fontWeight:600, marginBottom:4, color:'var(--navy)' }}>Bill To:</div>
                <div style={{ fontWeight:500 }}>{c.full_name}</div>
                <div style={{ color:'var(--text2)' }}>Customer ID: {c.customer_code}</div>
                <div style={{ color:'var(--text2)' }}>Ph: {c.phone}</div>
                {c.address && <div style={{ color:'var(--text2)' }}>{c.address}</div>}
              </div>
              {saleBill.items && saleBill.items.length > 0 && (
                <table style={{ width:'100%', borderCollapse:'collapse', marginBottom:12, fontSize:12 }}>
                  <thead>
                    <tr style={{ background:'var(--navy)', color:'#fff' }}>
                      <th style={{ padding:'6px 8px' }}>#</th>
                      <th style={{ padding:'6px 8px', textAlign:'left' }}>Product</th>
                      <th style={{ padding:'6px 8px', textAlign:'center' }}>Qty</th>
                      <th style={{ padding:'6px 8px', textAlign:'right' }}>Unit Price</th>
                      <th style={{ padding:'6px 8px', textAlign:'right' }}>Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {saleBill.items.map((item, i) => (
                      <tr key={i} style={{ borderBottom:'1px solid var(--border)' }}>
                        <td style={{ padding:'6px 8px', color:'var(--text2)', textAlign:'center' }}>{i+1}</td>
                        <td style={{ padding:'6px 8px' }}>
                          <div>{item.product?.name || '—'}</div>
                          {item.serial_number && (
                            <div style={{ fontSize:11, color:'var(--text2)', fontFamily:'monospace', marginTop:2 }}>
                              S/N: {item.serial_number}
                            </div>
                          )}
                          {item.product?.warranty_months > 0 && (
                            <div style={{ fontSize:10, color:'var(--navy)' }}>({item.product.warranty_months} months Warranty)</div>
                          )}
                        </td>
                        <td style={{ padding:'6px 8px', textAlign:'center' }}>{item.qty}</td>
                        <td style={{ padding:'6px 8px', textAlign:'right' }}>₹{Number(item.unit_price).toLocaleString('en-IN')}</td>
                        <td style={{ padding:'6px 8px', textAlign:'right', fontWeight:500 }}>₹{Number(item.qty*item.unit_price).toLocaleString('en-IN')}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              <div style={{ marginLeft:'auto', maxWidth:260, borderTop:'1px solid var(--border)', paddingTop:8, marginBottom:12, fontSize:12 }}>
                <div style={{ display:'flex', justifyContent:'space-between', color:'var(--text2)', marginBottom:4 }}><span>Subtotal</span><span>₹{Number(saleBill.subtotal).toLocaleString('en-IN')}</span></div>
                {discAmt > 0 && (
                  <div style={{ display:'flex', justifyContent:'space-between', color:'green', marginBottom:4 }}>
                    <span>{isExchange ? 'Under Exchange' : 'Discount'}</span>
                    <span>− ₹{discAmt.toLocaleString('en-IN',{minimumFractionDigits:2,maximumFractionDigits:2})}</span>
                  </div>
                )}
                <div style={{ display:'flex', justifyContent:'space-between', fontSize:15, fontWeight:700, color:'var(--navy)', marginTop:8, paddingTop:8, borderTop:'2px solid var(--navy)' }}><span>Total</span><span>₹{Number(saleBill.total).toLocaleString('en-IN')}</span></div>
              </div>
              <div style={{ fontSize:12, display:'flex', gap:20, flexWrap:'wrap' }}>
                <span><strong>Payment Mode:</strong> {saleBill.payment_mode}</span>
                <span><strong>Status:</strong> <span className={`badge ${saleBill.payment_status === 'paid' ? 'b-ok' : saleBill.payment_status === 'partial' ? 'b-warn' : 'b-err'}`}>{saleBill.payment_status}</span></span>
              </div>
            </div>
          );
        })()}
      </Modal>

      {/* Edit Customer Modal */}
      <Modal open={showEdit} onClose={() => setShowEdit(false)} title="Edit Customer Details"
        footer={<><button className="btn" onClick={() => setShowEdit(false)}>Cancel</button><button className="btn btn-primary" onClick={saveEdit} disabled={editSaving}>{editSaving ? 'Saving…' : 'Save Changes'}</button></>}
      >
        <div className="fg-2">
          <F label="Salutation"><select value={editForm.salutation || ''} onChange={setEF('salutation')}>
            <option value="">—</option>
            {['Mr','Ms','Dr'].map(s => <option key={s}>{s}</option>)}
          </select></F>
          <F label="Full Name" req><input value={editForm.full_name || ''} onChange={setEF('full_name')} placeholder="Customer full name"/></F>
          <F label="Address" req full><textarea value={editForm.address || ''} onChange={setEF('address')} placeholder="Door No, Street, Area" style={{ minHeight:55 }}/></F>
          <F label="State" req><select value={editForm.state || 'Kerala'} onChange={e => setEditForm(f => ({ ...f, state: e.target.value, district: getDistricts(e.target.value)[0] || '' }))}>
            {STATES.map(s => <option key={s}>{s}</option>)}
          </select></F>
          <F label="District" req><select value={editForm.district || ''} onChange={setEF('district')}>
            {getDistricts(editForm.state || 'Kerala').map(d => <option key={d}>{d}</option>)}
          </select></F>
          <F label="Pincode"><input value={editForm.pincode || ''} onChange={setEF('pincode')} placeholder="6-digit pincode" maxLength={6}/></F>
          <F label="Mobile Number" req><input value={editForm.phone || ''} onChange={setEF('phone')} placeholder="10-digit mobile"/></F>
          <F label="Email ID" full><input type="email" value={editForm.email || ''} onChange={setEF('email')} placeholder="email@example.com"/></F>
          <F label="Status"><select value={editForm.status || 'active'} onChange={setEF('status')}>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select></F>
        </div>
      </Modal>
    </div>
  );
}
